/**
 * EditInboxDialog - Diálogo de edição de inbox
 * Permite configurar nome, identificador, credenciais, assinatura e controle de acesso
 * Suporta vinculação de agentes específicos para acesso exclusivo
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { PhoneInput } from '@/components/ui/phone-input';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ToggleIconSwitch } from '@/components/ui/toggle-icon-switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Pen, AlertTriangle, Server, Users, ShieldCheck, Tags, Star, HelpCircle, Copy } from 'lucide-react';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useAvailableInstances } from '@/hooks/useProviderInstances';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { LabelManager } from '@/components/tenant/labels/LabelManager';
import { useInboxGroups, useSetInboxGroup, useInboxGroupId } from '@/hooks/useInboxGroups';
import { logger } from '@/lib/logger';
import { generateWebhookVerifyToken } from '@/lib/supabaseHelpers';


// Interface para agente do tenant (para listagem de vinculação)
interface TenantAgent {
  id: string;
  full_name: string | null;
  email: string | null;
}

// Interface para campo do schema de credenciais
interface CredentialSchemaField {
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
  order?: number; // Ordem de exibição do campo (usado por meta-instagram)
  readonly?: boolean; // Campo somente leitura (ignorado no contexto tenant para meta-instagram)
}

// Interface para configuração de versão do provider
interface ProviderVersionConfig {
  credentials_schema?: Record<string, CredentialSchemaField>;
  [key: string]: unknown;
}

// Interface para configurações da inbox (assinatura + agenda + espelhamento de grupos)
interface InboxSettings {
  signature_enabled?: boolean;
  signature_format?: string;
  /** Habilita/desabilita salvamento automático de contatos na agenda do WhatsApp */
  auto_save_to_agenda?: boolean;
  /** Habilita/desabilita espelhamento de grupos do WhatsApp (default: false) */
  group_mirroring_enabled?: boolean;
  [key: string]: unknown;
}

// Interface para dados da inbox
interface InboxData {
  id: string;
  name: string;
  identifier: string;
  channel_type: string;
  provider_version_id: string | null;
  settings: InboxSettings | null;
  tenant_id: string;
}

// Interface para instância vinculada
interface LinkedInstance {
  id: string;
  instance_name: string | null;
  status: string;
}

// Props do componente
interface EditInboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxId: string;
  onSuccess: () => void;
}

export function EditInboxDialog({ open, onOpenChange, inboxId, onSuccess }: EditInboxDialogProps) {
  // Hook de autenticação para obter nome do agente para preview
  const { profile, currentTenant, isRootAdmin, isTenantAdmin } = useAuth();
  
  // Hook de limites de recursos para verificar plano (espelhamento de grupos requer Professional+)
  const { limits } = useResourceLimits();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inbox, setInbox] = useState<InboxData | null>(null);
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [credentialsSchema, setCredentialsSchema] = useState<Record<string, CredentialSchemaField>>({});
  
  // Estados para configuração de assinatura
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [signatureFormat, setSignatureFormat] = useState('*{{agent_name}}:*');
  
  // Estado para configuração de salvamento automático na agenda do WhatsApp
  const [autoSaveToAgenda, setAutoSaveToAgenda] = useState(true);
  
  // Estado para espelhamento de grupos do WhatsApp (default: desabilitado)
  const [groupMirroringEnabled, setGroupMirroringEnabled] = useState(false);

  // Estado para vinculação da inbox a um Grupo de Inbox (filtro do dashboard)
  const [groupId, setGroupId] = useState<string | null>(null);
  const { data: inboxGroups = [] } = useInboxGroups(false);
  const { data: currentGroupId } = useInboxGroupId(inboxId);
  const setInboxGroupMutation = useSetInboxGroup();

  
  // Estados para validação de instance_id duplicado
  const [instanceIdConflict, setInstanceIdConflict] = useState<string | null>(null);
  const [checkingInstanceId, setCheckingInstanceId] = useState(false);
  
  // Estado para controle de gestão de instâncias
  // REGRA: TODOS os providers requerem gestão de instância, EXCETO webchat (Chat do Site)
  const [requiresInstance, setRequiresInstance] = useState(false);
  
  // Estados para seleção de instância quando não há instância vinculada
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [providerSlug, setProviderSlug] = useState<string | null>(null);
  
  // Estados para controle de acesso restrito
  const [accessRestricted, setAccessRestricted] = useState(false);
  const [linkedAgents, setLinkedAgents] = useState<string[]>([]); // IDs dos agentes vinculados
  const [linkedAgentSupervisors, setLinkedAgentSupervisors] = useState<Set<string>>(new Set()); // IDs dos agentes supervisores
  const [tenantAgents, setTenantAgents] = useState<TenantAgent[]>([]); // Lista de agentes do tenant
  const [loadingAgents, setLoadingAgents] = useState(false);
  
  // Estado para armazenar dados da instância vinculada
  const [linkedInstance, setLinkedInstance] = useState<LinkedInstance | null>(null);
  
  // Estados para exibir imagem de ajuda dos campos Meta Instagram
  const [showInstagramIdHelp, setShowInstagramIdHelp] = useState(false);
  const [showPageIdHelp, setShowPageIdHelp] = useState(false);
  const [showAccessTokenHelp, setShowAccessTokenHelp] = useState(false);
  const [showAppSecretHelp, setShowAppSecretHelp] = useState(false);

  // Carrega dados da inbox quando o dialog abre
  useEffect(() => {
    if (open && inboxId) {
      loadInboxData();
    }
  }, [open, inboxId]);

  // Hook para buscar instâncias disponíveis (apenas quando não há instância vinculada)
  const { availableInstances, useInstance, isLoading: loadingInstances } = useAvailableInstances(
    inbox?.tenant_id,
    providerSlug
  );

  // Reset do estado quando o dialog fecha
  useEffect(() => {
    if (!open) {
      setInbox(null);
      setName('');
      setIdentifier('');
      setCredentials({});
      setCredentialsSchema({});
      setSignatureEnabled(false);
      setSignatureFormat('*{{agent_name}}:*');
      setInstanceIdConflict(null);
      setCheckingInstanceId(false);
      setLinkedInstance(null);
      setSelectedInstanceId(null);
      setProviderSlug(null);
      setAccessRestricted(false);
      setLinkedAgents([]);
      setLinkedAgentSupervisors(new Set());
      setTenantAgents([]);
      setGroupMirroringEnabled(false);
      setGroupId(null);
      setLoading(true);

    }
  }, [open]);

  // Sincroniza groupId local quando o grupo atual da inbox é carregado
  useEffect(() => {
    if (open && currentGroupId !== undefined) {
      setGroupId(currentGroupId ?? null);
    }
  }, [open, currentGroupId]);


  /**
   * Carrega lista de agentes do tenant para seleção de acesso restrito
   * Busca membros ativos através da tabela tenant_members com join para profiles
   */
  const loadTenantAgents = useCallback(async (tenantId: string) => {
    setLoadingAgents(true);
    try {
      // Busca membros do tenant através da tabela tenant_members
      // que relaciona user_id com tenant_id, fazendo join com profiles
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/tenant_members?tenant_id=eq.${tenantId}&is_active=eq.true&select=user_id,profiles:user_id(id,full_name,email)`,
        {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );
      
      if (!response.ok) {
        logger.error('EditInboxDialog', 'Erro ao carregar agentes do tenant');
        return;
      }
      
      // Resultado vem no formato: [{ user_id, profiles: { id, full_name, email } }, ...]
      const data = await response.json() as Array<{ 
        user_id: string; 
        profiles: { id: string; full_name: string | null; email: string | null } | null 
      }>;
      
      // Mapeia para o formato TenantAgent esperado, filtrando profiles nulos
      const agents: TenantAgent[] = (data || [])
        .filter(item => item.profiles !== null)
        .map(item => ({
          id: item.profiles!.id,
          full_name: item.profiles!.full_name,
          email: item.profiles!.email,
        }));
      
      setTenantAgents(agents);
    } catch (error) {
      logger.error('EditInboxDialog', 'Erro ao carregar agentes', { error: String(error) });
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  // Debounce para verificar conflito de instance_id ao digitar
  useEffect(() => {
    const instanceId = credentials['instance_id'];
    
    // Se não tem instance_id, limpa estado de conflito
    if (!instanceId) {
      setInstanceIdConflict(null);
      return;
    }
    
    // Debounce de 500ms para evitar chamadas excessivas
    const timeoutId = setTimeout(() => {
      checkInstanceIdConflict(instanceId);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [credentials['instance_id'], inboxId, inbox?.tenant_id]);

  /**
   * Verifica se o instance_id já está em uso por outra inbox ativa do mesmo tenant
   * Exclui a inbox atual da verificação para permitir manter o mesmo instance_id
   */
  const checkInstanceIdConflict = async (instanceId: string) => {
    if (!instanceId || !inboxId || !inbox?.tenant_id) {
      setInstanceIdConflict(null);
      return;
    }
    
    setCheckingInstanceId(true);
    try {
      // Busca inboxes ATIVAS do mesmo TENANT que usam o mesmo instance_id
      const { data: conflictingInboxes, error } = await supabase
        .from('inbox_credentials')
        .select(`
          inbox_id,
          inbox:inboxes!inner(id, name, status, tenant_id)
        `)
        .filter('credentials->>instance_id', 'eq', instanceId)
        .eq('inbox.status', 'active')
        .eq('inbox.tenant_id', inbox.tenant_id) // Escopo do tenant
        .neq('inbox_id', inboxId); // Excluir inbox atual
      
      if (error) {
        console.error('[EditInboxDialog] Erro ao verificar conflito de instance_id:', error);
        setInstanceIdConflict(null);
        return;
      }
      
      // Se encontrou outra inbox ativa com o mesmo instance_id, define conflito
      if (conflictingInboxes && conflictingInboxes.length > 0) {
        const conflictingInbox = conflictingInboxes[0] as { inbox_id: string; inbox: { id: string; name: string; status: string; tenant_id: string } | null };
        const conflictingName = conflictingInbox.inbox?.name || 'Desconhecida';
        console.warn(`[EditInboxDialog] Conflito de instance_id detectado: "${instanceId}" já está em uso pela inbox "${conflictingName}"`);
        setInstanceIdConflict(conflictingName);
      } else {
        setInstanceIdConflict(null);
      }
    } catch (error) {
      console.error('[EditInboxDialog] Erro ao verificar instance_id:', error);
      setInstanceIdConflict(null);
    } finally {
      setCheckingInstanceId(false);
    }
  };

  // Helper para processar formato array { fields: [...] } do config_schema
  const processArraySchema = (fields: Array<{
    name: string;
    label?: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
    description?: string;
  }>): Record<string, CredentialSchemaField> => {
    const schema: Record<string, CredentialSchemaField> = {};
    fields.forEach((field) => {
      schema[field.name] = {
        label: field.label || field.name,
        type: field.type || 'text',
        required: field.required !== false,
        placeholder: field.placeholder || field.label || field.name,
        description: field.description,
      };
    });
    return schema;
  };

  // Carrega dados da inbox do banco de dados
  const loadInboxData = async () => {
    setLoading(true);
    try {
      // Busca dados da inbox incluindo settings e provider (para fallback do config_schema e verificação de gestão de instância)
      const { data: inboxData, error: inboxError } = await supabase
        .from('inboxes')
        .select(`
          id, name, identifier, channel_type, provider_version_id, settings, provider_id, tenant_id,
          provider:api_providers!provider_id (id, slug, config_schema)
        `)
        .eq('id', inboxId)
        .single();

      if (inboxError) throw inboxError;
      
          // Verifica se o provider requer gestão de instâncias
          // REGRA: TODOS os providers requerem gestão, EXCETO webchat e providers Meta (configuração manual de credenciais)
          const PROVIDERS_WITHOUT_INSTANCE_MANAGEMENT = ['webchat', 'meta-whatsapp', 'meta-instagram'];
          const providerData = inboxData.provider as { id: string; slug: string; config_schema: unknown } | null;
          const currentProviderSlug = providerData?.slug;
          const providerRequiresInstance = currentProviderSlug 
            ? !PROVIDERS_WITHOUT_INSTANCE_MANAGEMENT.includes(currentProviderSlug) 
            : false;
          setRequiresInstance(providerRequiresInstance);
      
      // Salva o slug do provider para buscar instâncias disponíveis
      setProviderSlug(currentProviderSlug || null);

      // Trata settings como InboxSettings ou null
      const settings = inboxData.settings as InboxSettings | null;
      
      setInbox({
        id: inboxData.id,
        name: inboxData.name,
        identifier: inboxData.identifier,
        channel_type: inboxData.channel_type,
        provider_version_id: inboxData.provider_version_id,
        settings,
        tenant_id: inboxData.tenant_id,
      });
      setName(inboxData.name);
      setIdentifier(inboxData.identifier);
      
      // Carrega configurações de assinatura e agenda do settings
      if (settings) {
        setSignatureEnabled(settings.signature_enabled || false);
        setSignatureFormat(settings.signature_format || '_*{{agent_name}}:*_');
        // auto_save_to_agenda: default true quando ausente (comportamento original)
        setAutoSaveToAgenda(settings.auto_save_to_agenda !== false);
        // group_mirroring_enabled: default false quando ausente
        setGroupMirroringEnabled(settings.group_mirroring_enabled === true);
      }

      // Fetch credentials
      const { data: credentialsData, error: credentialsError } = await supabase
        .from('inbox_credentials')
        .select('credentials')
        .eq('inbox_id', inboxId)
        .maybeSingle();

      if (credentialsError) throw credentialsError;

      if (credentialsData?.credentials) {
        let loadedCredentials = credentialsData.credentials as Record<string, string>;
        
        // AUTO-GERA webhook_verify_token para meta-instagram se ausente
        // Isso corrige inboxes legadas que foram criadas sem o token
        if (currentProviderSlug === 'meta-instagram' && !loadedCredentials.webhook_verify_token) {
          loadedCredentials = {
            ...loadedCredentials,
            webhook_verify_token: generateWebhookVerifyToken()
          };
          logger.info('EditInboxDialog', 'Token de verificação gerado automaticamente para meta-instagram');
        }
        
        setCredentials(loadedCredentials);
      }

      // Fetch provider version config for credentials schema
      let schema: Record<string, CredentialSchemaField> | null = null;

      // Primeiro tenta buscar de provider_versions.config.credentials_schema
      if (inboxData.provider_version_id) {
        const { data: versionData, error: versionError } = await supabase
          .from('provider_versions')
          .select('config')
          .eq('id', inboxData.provider_version_id)
          .single();

        if (!versionError) {
          const config = versionData?.config as ProviderVersionConfig | null;
          if (config?.credentials_schema) {
            schema = config.credentials_schema;
          }
        }
      }

      // Fallback: busca de api_providers.config_schema se não encontrou em provider_versions
      if (!schema && inboxData.provider) {
        const providerData = inboxData.provider as { id: string; config_schema: unknown } | null;
        const configSchema = providerData?.config_schema;
        
        if (configSchema) {
          // Verifica se é formato array { fields: [...] }
          if (typeof configSchema === 'object' && 'fields' in (configSchema as Record<string, unknown>) && Array.isArray((configSchema as { fields: unknown[] }).fields)) {
            schema = processArraySchema((configSchema as { fields: Array<{ name: string; label?: string; type?: string; required?: boolean; placeholder?: string; description?: string }> }).fields);
          } else if (typeof configSchema === 'object') {
            // Formato objeto padrão
            schema = configSchema as Record<string, CredentialSchemaField>;
          }
        }
      }

      if (schema) {
        setCredentialsSchema(schema);
      }
      
      // Busca instância vinculada a esta inbox (se provider requer gestão de instância)
      if (providerRequiresInstance) {
        const { data: instanceData, error: instanceError } = await supabase
          .from('provider_instances')
          .select('id, instance_name, status')
          .eq('inbox_id', inboxId)
          .maybeSingle();
        
        if (!instanceError && instanceData) {
          setLinkedInstance(instanceData);
        }
      }
      
      // Carrega configuração de acesso restrito da inbox
      // Busca via REST API para evitar problemas de tipo
      const accessResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/inboxes?id=eq.${inboxId}&select=access_restricted`,
        {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );
      
      if (accessResponse.ok) {
        const accessData = await accessResponse.json() as Array<{ access_restricted: boolean | null }>;
        if (accessData?.[0]) {
          setAccessRestricted(accessData[0].access_restricted === true);
        }
      }
      
      // Carrega agentes vinculados a esta inbox (inbox_agents) com is_supervisor
      const agentsResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/inbox_agents?inbox_id=eq.${inboxId}&select=user_id,is_supervisor`,
        {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );
      
      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json() as Array<{ user_id: string; is_supervisor: boolean }>;
        setLinkedAgents((agentsData || []).map(a => a.user_id));
        // Carrega supervisores - agentes com is_supervisor=true
        const supervisorIds = (agentsData || [])
          .filter(a => a.is_supervisor === true)
          .map(a => a.user_id);
        setLinkedAgentSupervisors(new Set(supervisorIds));
      }
      
      // Carrega lista de agentes do tenant para seleção
      await loadTenantAgents(inboxData.tenant_id);
      
    } catch (error) {
      logger.error('EditInboxDialog', 'Erro ao carregar dados da inbox', { error: String(error) });
      toast.error('Erro ao carregar dados da caixa de entrada');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  // Salva alterações da inbox
  const handleSave = async () => {
    if (!inbox || !name || !identifier) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      // Se o usuário selecionou uma instância (e não tinha antes), vincula a instância
      if (selectedInstanceId && !linkedInstance) {
        logger.info('EditInboxDialog', 'Vinculando instância selecionada', { instanceId: selectedInstanceId });
        await useInstance.mutateAsync({
          instanceId: selectedInstanceId,
          inboxId: inboxId
        });
        logger.info('EditInboxDialog', 'Instância vinculada com sucesso');
      }
      
      // Monta novo settings com assinatura e configuração de agenda (tipado como objeto JSON)
      const updatedSettings: { [key: string]: string | boolean | undefined } = {
        ...((inbox?.settings || {}) as Record<string, string | boolean | undefined>),
        signature_enabled: signatureEnabled,
        signature_format: signatureFormat,
        auto_save_to_agenda: autoSaveToAgenda,
        group_mirroring_enabled: groupMirroringEnabled,
      };

      // Update inbox com settings e access_restricted via REST API
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      
      // Atualiza inbox com access_restricted
      const updateResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/inboxes?id=eq.${inboxId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Prefer': 'return=minimal',
          },
          body: JSON.stringify({
            name,
            // Normaliza identificador para canais de telefone antes de salvar
            identifier: isPhoneChannel ? normalizePhoneIdentifier(identifier) : identifier.trim(),
            access_restricted: accessRestricted,
            settings: updatedSettings,
          }),
        }
      );
      
      if (!updateResponse.ok) {
        throw new Error('Erro ao atualizar inbox');
      }

      // Atualiza vinculação ao Grupo de Inbox apenas quando o valor mudou
      if ((currentGroupId ?? null) !== (groupId ?? null)) {
        await setInboxGroupMutation.mutateAsync({ inboxId, groupId });
      }


      // Upsert credentials
      if (Object.keys(credentials).length > 0) {
        const { error: credError } = await supabase
          .from('inbox_credentials')
          .upsert(
            {
              inbox_id: inboxId,
              credentials,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'inbox_id' }
          );

        if (credError) throw credError;
      }
      
      // Gerencia vinculações de agentes (inbox_agents)
      // Primeiro remove todos os existentes
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/inbox_agents?inbox_id=eq.${inboxId}`,
        {
          method: 'DELETE',
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      
      // Insere os novos agentes vinculados com flag is_supervisor (se houver e se access_restricted estiver ativo)
      if (accessRestricted && linkedAgents.length > 0 && inbox?.tenant_id) {
        const agentInserts = linkedAgents.map(userId => ({
          inbox_id: inboxId,
          user_id: userId,
          tenant_id: inbox.tenant_id,
          is_supervisor: linkedAgentSupervisors.has(userId), // Flag de supervisor por inbox
        }));
        
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/inbox_agents`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              'Authorization': `Bearer ${accessToken}`,
              'Prefer': 'return=minimal',
            },
            body: JSON.stringify(agentInserts),
          }
        );
      }

      toast.success('Caixa de entrada atualizada com sucesso!');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      logger.error('EditInboxDialog', 'Erro ao atualizar inbox', { error: String(error) });
      toast.error('Erro ao atualizar caixa de entrada');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Alterna a seleção de um agente na lista de vinculados
   * Remove também do set de supervisores se removido
   */
  const toggleAgentSelection = (agentId: string) => {
    setLinkedAgents(prev => {
      if (prev.includes(agentId)) {
        // Remove agente e seu status de supervisor
        setLinkedAgentSupervisors(s => {
          const newSet = new Set(s);
          newSet.delete(agentId);
          return newSet;
        });
        return prev.filter(id => id !== agentId);
      }
      return [...prev, agentId];
    });
  };
  
  /**
   * Alterna o status de supervisor de um agente vinculado
   */
  const toggleAgentSupervisor = (agentId: string) => {
    setLinkedAgentSupervisors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
  };

  /**
   * Retorna label apropriado para o campo identificador baseado no tipo de canal
   * Cada canal tem um identificador diferente (número, username, page ID, etc.)
   */
  const getIdentifierLabel = () => {
    switch (inbox?.channel_type) {
      case 'whatsapp':
        return 'Número do WhatsApp';
      case 'whatsapp_oficial':
        return 'Número do WhatsApp Oficial';
      case 'telegram':
        return 'Número do Telegram';
      case 'instagram':
        return 'Username do Instagram';
      case 'messenger':
        return 'ID da Página';
      case 'webchat':
        return 'Domínio do Site';
      default:
        return 'Identificador';
    }
  };

  /**
   * Verifica se o canal utiliza número de telefone como identificador
   * Canais como WhatsApp, Telegram e WhatsApp Oficial usam telefone
   */
  const isPhoneChannel = useMemo(() => {
    return inbox?.channel_type === 'whatsapp' || 
           inbox?.channel_type === 'telegram' || 
           inbox?.channel_type === 'whatsapp_oficial';
  }, [inbox?.channel_type]);

  /**
   * Retorna placeholder apropriado para canais que não usam telefone
   */
  const getIdentifierPlaceholder = () => {
    switch (inbox?.channel_type) {
      case 'instagram':
        return '@username';
      case 'messenger':
        return 'ID da página';
      case 'webchat':
        return 'chat.seusite.com';
      default:
        return 'Identificador';
    }
  };

  // Filtra client_token (preenchido automaticamente via variável de ambiente)
  // Ordena campos por 'order' quando disponível no schema (importante para meta-instagram)
  const credentialFields = Object.entries(credentialsSchema)
    .filter(([key]) => key !== 'client_token') // Oculta client_token
    .sort(([, a], [, b]) => {
      // Ordena por 'order' se existir, senão mantém ordem original
      const orderA = (a as CredentialSchemaField & { order?: number }).order ?? 999;
      const orderB = (b as CredentialSchemaField & { order?: number }).order ?? 999;
      return orderA - orderB;
    })
    .map(([key, config]) => ({
      key,
      label: config.label || key,
      type: config.type || 'text',
      required: config.required !== false,
      placeholder: config.placeholder || config.label || key,
      description: config.description,
    }));
  
  // Verifica se é provider meta-instagram (mostra credenciais mesmo com instância vinculada)
  const isMetaInstagram = providerSlug === 'meta-instagram';
  
  // Define se deve mostrar seção de credenciais
  // Meta Instagram: sempre mostra credenciais (fluxo 2 etapas)
  // Outros providers: só mostra se não requer instância
  // Credenciais sensíveis visíveis apenas para root_admin
  // tenant_admin pode editar credenciais APENAS do canal meta-instagram (renovação de token)
  const shouldShowCredentials = (isRootAdmin || (isTenantAdmin && isMetaInstagram)) && credentialFields.length > 0 && (!requiresInstance || isMetaInstagram);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] p-0 gap-0 overflow-hidden flex flex-col"
        preventCloseOnOutsideInteraction
      >
        {/* ===== HEADER PREMIUM (padrão modais da aba Instância) ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Pen className="h-3.5 w-3.5 text-primary" />
              </div>
              Configurações da Inbox
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Edite as informações e credenciais da sua inbox.
            </DialogDescription>
          </DialogHeader>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="px-6 py-4 space-y-6 max-h-[65vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome da Inbox</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: WhatsApp Vendas"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="identifier">{getIdentifierLabel()}</Label>
                {isPhoneChannel ? (
                  <PhoneInput
                    id="identifier"
                    value={identifier}
                    onChange={setIdentifier}
                    placeholder="(11) 99999-9999"
                  />
                ) : (
                  <Input
                    id="identifier"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder={getIdentifierPlaceholder()}
                  />
                )}
              </div>

              {/* Vinculação a Grupo de Inbox — usado como filtro no dashboard */}
              <div className="space-y-2">
                <Label htmlFor="inbox-group">Grupo</Label>
                <Select
                  value={groupId ?? '__none__'}
                  onValueChange={(v) => setGroupId(v === '__none__' ? null : v)}
                >
                  <SelectTrigger id="inbox-group">
                    <SelectValue placeholder="Sem grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sem grupo</SelectItem>
                    {inboxGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        <div className="flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 rounded-full inline-block"
                            style={{ backgroundColor: g.color }}
                          />
                          <span>{g.name}</span>
                          {!g.is_active && (
                            <span className="text-[10px] text-muted-foreground">(inativo)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Vincule a inbox a um grupo para filtrar conversas no dashboard.
                </p>
              </div>
            </div>


            {/* Seção de Instância Vinculada - exibe quando provider requer gestão de instâncias */}
            {requiresInstance && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Server className="h-4 w-4 text-muted-foreground" />
                    Instância Vinculada
                  </Label>
                  
                  {linkedInstance ? (
                    // === CASO 1: JÁ TEM INSTÂNCIA - VISUALIZAÇÃO ESTÁTICA (somente leitura) ===
                    <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                      {/* Badge com ID (8 primeiros caracteres) */}
                      <Badge 
                        variant="outline" 
                        className="font-mono text-[10px] px-1.5 py-0.5 text-muted-foreground border-muted-foreground/30"
                      >
                        {linkedInstance.id.substring(0, 8).toUpperCase()}
                      </Badge>
                      
                      {/* Nome da instância */}
                      <span className="text-sm font-medium flex-1">
                        {linkedInstance.instance_name || 'Sem nome'}
                      </span>
                      
                      {/* Status badge */}
                      <Badge 
                        variant={linkedInstance.status === 'in_use' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {linkedInstance.status === 'in_use' ? 'Em uso' : linkedInstance.status}
                      </Badge>
                    </div>
                  ) : (
                    // === CASO 2: SEM INSTÂNCIA - DROPDOWN DE SELEÇÃO ===
                    <div className="space-y-2">
                      {loadingInstances ? (
                        <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Carregando instâncias...</span>
                        </div>
                      ) : availableInstances.length > 0 ? (
                        <Select 
                          value={selectedInstanceId || ''} 
                          onValueChange={setSelectedInstanceId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione uma instância..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableInstances.map((instance) => (
                              <SelectItem key={instance.id} value={instance.id}>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs text-muted-foreground">
                                    {instance.id.substring(0, 8).toUpperCase()}
                                  </span>
                                  <span>{instance.instance_name || 'Sem nome'}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm text-muted-foreground italic p-3 rounded-lg border bg-muted/30">
                          Nenhuma instância disponível. Aguarde liberação pelo administrador.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Seção de Credenciais - Exibe para providers sem gestão de instância OU meta-instagram (fluxo 2 etapas) */}
            {shouldShowCredentials && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-foreground">Credenciais de Integração</h4>
                  
                  {credentialFields.map((field) => (
                    <div key={field.key} className="space-y-2">
                      {/* Label com botão de ajuda para campos específicos */}
                      <div className="flex items-center gap-2">
                        <Label htmlFor={field.key}>
                          {field.label}
                          {field.required && <span className="text-destructive ml-1">*</span>}
                        </Label>
                        
                        {/* Botões de ajuda visual para campos Meta Instagram */}
                        {field.key === 'instagram_business_account_id' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                            onClick={() => setShowInstagramIdHelp(true)}
                            title="Clique para ver onde encontrar este campo no Meta"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Botão de ajuda para Token de Acesso (Meta Instagram) */}
                        {field.key === 'access_token' && isMetaInstagram && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                            onClick={() => setShowAccessTokenHelp(true)}
                            title="Clique para ver onde encontrar o Token de Acesso no Meta"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Botão de ajuda para ID da Página Facebook (Meta Instagram) */}
                        {field.key === 'page_id' && isMetaInstagram && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                            onClick={() => setShowPageIdHelp(true)}
                            title="Clique para ver onde encontrar o ID da Página Facebook"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Botão de ajuda para Chave Secreta do App (Meta Instagram) */}
                        {field.key === 'app_secret' && isMetaInstagram && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                            onClick={() => setShowAppSecretHelp(true)}
                            title="Clique para ver onde encontrar a Chave Secreta no Meta"
                          >
                            <HelpCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Campo webhook_verify_token - somente leitura com botão copiar */}
                      {field.key === 'webhook_verify_token' && credentials[field.key] ? (
                        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border">
                          <code className="flex-1 text-xs break-all font-mono text-foreground">
                            {credentials[field.key]}
                          </code>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => {
                              navigator.clipboard.writeText(credentials[field.key] || '');
                              toast.success('Token copiado!');
                            }}
                            title="Copiar token"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Input
                          id={field.key}
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={credentials[field.key] || ''}
                          onChange={(e) =>
                            setCredentials((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                          placeholder={field.placeholder}
                          className={field.key === 'instance_id' && instanceIdConflict ? 'border-destructive' : ''}
                        />
                      )}
                      
                      {/* Descrição do campo - ajusta texto para webhook_verify_token */}
                      {field.description && (
                        <p className="text-xs text-muted-foreground">
                          {field.key === 'webhook_verify_token' 
                            ? 'Token gerado automaticamente para validar o webhook no Meta' 
                            : field.description}
                        </p>
                      )}
                      
                      {/* Mensagem de conflito de instance_id */}
                      {field.key === 'instance_id' && instanceIdConflict && (
                        <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
                          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                          <p className="text-xs text-destructive">
                            A caixa de entrada "<strong>{instanceIdConflict}</strong>" já está usando esta instância. 
                            Desative ou desconecte "{instanceIdConflict}" antes de continuar.
                          </p>
                        </div>
                      )}
                      
                      {/* Indicador de verificação em andamento */}
                      {field.key === 'instance_id' && checkingInstanceId && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Verificando disponibilidade...
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Seção: Assinatura nas Mensagens */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Pen className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-foreground">Assinatura nas Mensagens</h4>
              </div>
              
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="signature-enabled">Habilitar assinatura do agente</Label>
                  <p className="text-xs text-muted-foreground">
                    Adiciona identificação do agente no início de cada mensagem
                  </p>
                </div>
                <ToggleIconSwitch
                  id="signature-enabled"
                  checked={signatureEnabled}
                  onCheckedChange={setSignatureEnabled}
                />
              </div>
              
              {signatureEnabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="signature-format">Formato da assinatura</Label>
                    <Input
                      id="signature-format"
                      value={signatureFormat}
                      onChange={(e) => setSignatureFormat(e.target.value)}
                      placeholder="*{{agent_name}}:*"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use <code className="bg-muted px-1 py-0.5 rounded">{'{{agent_name}}'}</code> para o nome do agente.
                      Use <code className="bg-muted px-1 py-0.5 rounded">*texto*</code> para negrito (WhatsApp).
                    </p>
                  </div>
                  
                  {/* Prévia da assinatura */}
                  <div className="rounded-md border bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground mb-2">Prévia da mensagem:</p>
                    <div className="text-sm space-y-1">
                      <p className="font-semibold">
                        {signatureFormat.replace('{{agent_name}}', profile?.full_name || 'Nome do Agente').replace(/\*/g, '')}
                      </p>
                      <p className="text-muted-foreground">Sua mensagem aqui...</p>
                    </div>
                  </div>
                </>
              )}
              </div>
              
            {/* Seção: Agenda do WhatsApp (apenas para canais WhatsApp) */}
            {(inbox?.channel_type === 'whatsapp' || inbox?.channel_type === 'whatsapp_oficial') && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="icofont-smart-phone text-muted-foreground" style={{ fontSize: '16px' }} />
                    <h4 className="text-sm font-medium text-foreground">Agenda do WhatsApp</h4>
                  </div>
                  
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="auto-save-agenda">Salvar contatos automaticamente na agenda</Label>
                      <p className="text-xs text-muted-foreground">
                        Salva novos contatos na agenda do celular conectado ao WhatsApp automaticamente
                      </p>
                    </div>
                    <ToggleIconSwitch
                      id="auto-save-agenda"
                      checked={autoSaveToAgenda}
                      onCheckedChange={setAutoSaveToAgenda}
                    />
                  </div>
                  
                  {!autoSaveToAgenda && (
                    <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 border">
                      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground">
                        Com esta opção desabilitada, contatos novos <strong>não serão salvos automaticamente</strong> na agenda do celular. 
                        Você ainda poderá salvar manualmente pelo painel do contato.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Seção: Espelhamento de Grupos (apenas para canais WhatsApp) */}
            {(inbox?.channel_type === 'whatsapp' || inbox?.channel_type === 'whatsapp_oficial') && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium text-foreground">Espelhamento de Grupos</h4>
                  </div>
                  
                  {/*
                   * CHANGELOG:
                   *   RELEASE: 2026-10-05 - Espelhamento de grupos liberado para todos os planos
                   *            (removida trava Profissional+/Enterprise; padrão segue desligado).
                   */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="group-mirroring" className="flex items-center gap-2">
                        Habilitar espelhamento de grupos
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Receba mensagens dos grupos do WhatsApp conectado nesta inbox
                      </p>
                    </div>
                    <ToggleIconSwitch
                      id="group-mirroring"
                      checked={groupMirroringEnabled}
                      onCheckedChange={setGroupMirroringEnabled}
                    />
                  </div>

                  {!groupMirroringEnabled && (
                    <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50 border">
                      <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground">
                        Com esta opção desabilitada, mensagens de grupos do WhatsApp <strong>não serão espelhadas</strong> nesta inbox.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Seção: Controle de Acesso */}
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-foreground">Controle de Acesso</h4>
              </div>
              
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="access-restricted">Restringir acesso a agentes específicos</Label>
                  <p className="text-xs text-muted-foreground">
                    Apenas os agentes selecionados poderão acessar esta caixa de entrada
                  </p>
                </div>
                <ToggleIconSwitch
                  id="access-restricted"
                  checked={accessRestricted}
                  onCheckedChange={setAccessRestricted}
                />
              </div>
              
              {accessRestricted && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Agentes com acesso
                    </Label>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" /> = Supervisor
                    </p>
                  </div>
                  
                  {loadingAgents ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Carregando agentes...</span>
                    </div>
                  ) : tenantAgents.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                      {tenantAgents.map((agent) => {
                        const isLinked = linkedAgents.includes(agent.id);
                        const isSupervisor = linkedAgentSupervisors.has(agent.id);
                        
                        return (
                          <div
                            key={agent.id}
                            className="flex items-center space-x-3 py-1"
                          >
                            <Checkbox
                              id={`agent-${agent.id}`}
                              checked={isLinked}
                              onCheckedChange={() => toggleAgentSelection(agent.id)}
                            />
                            <label
                              htmlFor={`agent-${agent.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                            >
                              {agent.full_name || agent.email || 'Sem nome'}
                              {agent.email && agent.full_name && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({agent.email})
                                </span>
                              )}
                            </label>
                            {/* Toggle de Supervisor - só aparece se agente está vinculado */}
                            {isLinked && (
                              <button
                                type="button"
                                onClick={() => toggleAgentSupervisor(agent.id)}
                                className="p-1 rounded hover:bg-muted transition-colors"
                                title={isSupervisor ? 'Remover supervisão desta inbox' : 'Tornar supervisor desta inbox'}
                              >
                                <Star 
                                  className={`h-4 w-4 transition-colors ${
                                    isSupervisor 
                                      ? 'text-yellow-500 fill-yellow-500' 
                                      : 'text-muted-foreground hover:text-yellow-500'
                                  }`} 
                                />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic p-3 rounded-lg border bg-muted/30">
                      Nenhum agente encontrado no tenant.
                    </p>
                  )}
                  
                  {accessRestricted && linkedAgents.length === 0 && tenantAgents.length > 0 && (
                    <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        Selecione ao menos um agente para ter acesso a esta inbox.
                      </p>
                    </div>
                  )}
                  
                  {linkedAgents.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {linkedAgents.length} agente(s) selecionado(s)
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* TEMPORARIAMENTE DESABILITADO - Funcionalidade de etiquetas parcialmente descontinuada
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Tags className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium text-foreground">Etiquetas</h4>
              </div>
              <p className="text-xs text-muted-foreground">
                Gerencie as etiquetas disponíveis para esta caixa de entrada.
              </p>
              <LabelManager 
                inboxId={inboxId} 
                className="border-0 shadow-none p-0"
              />
            </div>
            */}
            </div>
          </div>
        )}

        {/* ===== FOOTER PREMIUM (padrão modais da aba Instância) ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={loading || saving || !!instanceIdConflict || checkingInstanceId}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar Alterações
          </Button>
        </div>
      </DialogContent>
      
      {/* Lightbox de ajuda para ID da Conta Instagram */}
      <ImageLightbox
        open={showInstagramIdHelp}
        onClose={() => setShowInstagramIdHelp(false)}
        imageUrl="/docs/meta-instagram-account-id-help.png"
        caption="Onde encontrar o ID da Conta do Instagram no Meta Developer Portal"
      />
      
      {/* Lightbox de ajuda para ID da Página Facebook */}
      <ImageLightbox
        open={showPageIdHelp}
        onClose={() => setShowPageIdHelp(false)}
        imageUrl="/docs/meta-instagram-page-id-help.png"
        caption="Onde encontrar o ID da Página Facebook no Meta Business Suite"
      />
      
      {/* Lightbox de ajuda para Token de Acesso */}
      <ImageLightbox
        open={showAccessTokenHelp}
        onClose={() => setShowAccessTokenHelp(false)}
        imageUrl="/docs/meta-instagram-access-token-help.png"
        caption="Onde encontrar o Token de Acesso no Meta Developer Portal"
      />
      
      {/* Lightbox de ajuda para Chave Secreta do App */}
      <ImageLightbox
        open={showAppSecretHelp}
        onClose={() => setShowAppSecretHelp(false)}
        imageUrl="/docs/meta-instagram-app-secret-help.png"
        caption="Onde encontrar a Chave Secreta do App no Meta Developer Portal"
      />
    </Dialog>
  );
}
